import MondayService from "@/services/MondayService.js";
import moment from "moment";
import Constants from "@/constants";
import { stringToColor, contrast } from "../../util/helper";

export const namespaced = true;

const LONG_DATE = "D MMM YYYY, h:mm A";
const SHORT_DATE = "D MMM YYYY";
const FETCH_NEW_RECORD_DELAY = 1000;

export const state = {
  boards: [],
  board_items: [],
  board_item_messages: []
};

export const mutations = {
  SET_BOARDS(state, boards) {
    state.boards = boards;
  },
  SET_BOARD_ITEMS(state, board_items) {
    state.board_items = board_items;
  },
  PUSH_BOARD_ITEMS(state, { board_items, toTop = false }) {
    if (!toTop) {
      state.board_items[0].items.push(...board_items.items);
    } else {
      state.board_items[0].items = [
        ...board_items.items,
        ...state.board_items[0].items
      ];
    }

    state.board_items[0].page_cursor = board_items.page_cursor;
  },
  SET_BOARD_ITEM_MESSAGES(state, board_item_messages) {
    board_item_messages
      .sort((a, b) => {
        moment(a.created_at, LONG_DATE).isSameOrBefore(
          moment(b.created_at, LONG_DATE)
        )
          ? 1
          : -1;
      })
      .map(update => {
        update.created_at = moment(update.created_at).format(LONG_DATE);
        update.creator = update.creator || { id: -4, name: "Automation Message" };
        if (update.replies) {
          update.replies.map(reply => {
            reply.created_at = moment(reply.created_at).format(LONG_DATE);
            update.creator = update.creator || { id: -4, name: "Automation Message" };
          });
        }
      });
    state.board_item_messages = board_item_messages;
  },
  UPDATE_BOARD_ITEM(state, { item_id, column_values, name }) {
    if (state.board_items && state.board_items.length > 0) {
      let item_to_update = state.board_items[0].items.find(
        i => i.id == item_id
      );

      if (item_to_update) {
        item_to_update.column_values = column_values;
        item_to_update = parseColumnValues(item_to_update, column_values);
        item_to_update.name = name;
        // console.log("item_to_update", item_to_update);
      }
    }
  },
  UPDATE_SINGLE_FIELD(state, { item_id, column_values, name }) {
    if (state.board_items && state.board_items.length > 0) {
      let item_to_update = state.board_items[0].items.find(
        i => i.id == item_id
      );

      if (item_to_update) {
        item_to_update.column_values = column_values;
        item_to_update = parseColumnValues(item_to_update, column_values);
        item_to_update.name = name;
        // console.log("item_to_update", item_to_update);
      }
    }
  }
};

export const actions = {
  list_boards({ commit, dispatch }) {
    dispatch("setIsLoading", true, { root: true });

    return MondayService.listBoards().then(response => {
      let boards = response.data.data.boards;
      commit("SET_BOARDS", boards);
      dispatch("setIsLoading", false, { root: true });
    });
  },
  list_board_items({ commit, dispatch }, { id, page_cursor }) {
    if (!page_cursor) {
      dispatch("setIsLoading", true, { root: true });
    }
    return MondayService.listBoardItems(id, page_cursor).then(response => {
      let boards = response.data.data.boards;

      mapBoardItems(boards);

      if (page_cursor) {
        commit("PUSH_BOARD_ITEMS", { board_items: boards[0] });
      } else {
        commit("SET_BOARD_ITEMS", boards);
        dispatch("setIsLoading", false, { root: true });
      }
    });
  },
  list_board_items_for_table({ commit, dispatch }, { board_id, item_id }) {
    return MondayService.listBoardItemsForTable(board_id, item_id).then(
      response => {
        let boards = response.data.data.boards;

        mapBoardItems(boards);

        commit("PUSH_BOARD_ITEMS", { board_items: boards[0], toTop: true });
        dispatch("setIsLoading", false, { root: true });
      }
    );
  },
  refresh_board_items({ commit, dispatch }, { id, page_cursor }) {
    return MondayService.listBoardItems(id, page_cursor).then(response => {
      let boards = response.data.data.boards;

      mapBoardItems(boards);

      if (boards[0].page_cursor) {
        dispatch("setIsLoading", true, { root: true });
        dispatch("refresh_board_items", {
          id,
          page_cursor: boards[0].page_cursor
        });
      } else {
        dispatch("setIsLoading", false, { root: true });
      }

      if (page_cursor) {
        commit("PUSH_BOARD_ITEMS", { board_items: boards[0] });
      } else {
        commit("SET_BOARD_ITEMS", boards);
      }
    });
  },
  get_board_item_messages({ commit, dispatch }, item_id) {
    dispatch("setIsLoading", true, { root: true });
    return MondayService.getBoardItemMessages(item_id).then(response => {
      let board_item_messages = response.data.data.items[0].updates;
      commit("SET_BOARD_ITEM_MESSAGES", board_item_messages);
      dispatch("setIsLoading", false, { root: true });
    });
  },
  send_board_item_message({ commit, dispatch }, { item_id, msg, files }) {
    dispatch("setIsLoading", true, { root: true });
    return MondayService.sendBoardItemMessage(item_id, msg, files).then(
      response => {
        let MIN_WAIT_TIME = 3500;
        let wait = MIN_WAIT_TIME;

        if (files && files.length > 0) {
          // files uploaded to monday does not appear in update immediately and there doesnt seem to be a callback event, so we wait a while before calling get updates (estimate based on file size and number of files)

          // calculate total file size
          let total_size = 0;
          for (let i = 0; i < files.length; i++) {
            let file = files[i];
            total_size += file.size;
          }
          let average_size_in_mb = total_size / 1000000 / files.length;
          wait = Math.floor(files.length * average_size_in_mb * MIN_WAIT_TIME); // estimated
          wait = wait < MIN_WAIT_TIME ? MIN_WAIT_TIME : wait;
        } else wait = 0;

        // console.log('wait: ', wait);
        setTimeout(() => {
          dispatch("get_board_item_messages", item_id);
          dispatch("setIsLoading", false, { root: true });
        }, wait);
      }
    );
  },
  delete_board_item_message({ commit, dispatch }, update_id) {
    // console.log('monday, delete update: ', update_id);
    return MondayService.deleteUpdate(update_id);
  },
  get_board_item({ commit, dispatch }, item_id) {
    return MondayService.getBoardItem(item_id).then(response => {
      // console.log("get_board_item", response.data.data.items[0]);
      let { column_values, name } = response.data.data.items[0];
      commit("UPDATE_BOARD_ITEM", { item_id, column_values, name });
    });
  },
  get_me({ commit }) {
    return MondayService.getMe();
  },
  // single column update
  update_column(
    { commit, dispatch },
    { board_id, item_id, column_id, column_type, value, column_values, name }
  ) {
    dispatch("setIsLoading", true, { root: true });
    return MondayService.updateColumn(
      board_id,
      item_id,
      column_id,
      column_type,
      value
    ).then(response => {
      // Give automation time to complete
      // setTimeout(() => {
      //   dispatch("get_board_item", item_id);
      // }, FETCH_NEW_RECORD_DELAY);
      commit("UPDATE_SINGLE_FIELD", { item_id, column_values, name });
      dispatch("setIsLoading", false, { root: true });
    });
  },
  add_new_item(
    { commit, dispatch },
    { board_id, item_name, group_id, column_values, board_columns }
  ) {
    dispatch("setIsLoading", true, { root: true });
    return MondayService.addNewItem(
      board_id,
      item_name,
      group_id,
      column_values,
      board_columns
    ).then(response => {
      dispatch("list_board_items_for_table", {
        board_id: board_id,
        item_id: response.data.data.create_item.id
      });
    });
  },
  update_item(
    { commit, state, dispatch },
    { board_id, item_id, column_values, board_columns, name }
  ) {
    return MondayService.updateItem(
      board_id,
      item_id,
      column_values,
      board_columns,
      name
    ).then(response => {
      // // Give automation time to complete
      // setTimeout(() => {
      //   dispatch("get_board_item", item_id);
      // }, FETCH_NEW_RECORD_DELAY);
      commit("UPDATE_SINGLE_FIELD", { item_id, column_values, name });
      dispatch("setIsLoading", false, { root: true });
    });
  },
  get_board_name({ state, dispatch }, { board_id, to }) {
    let title = "Power Up";
    if (state.boards && state.boards.length == 0) {
      // on refresh: boards not loaded yet, fetch boards, then match id for board name
      return dispatch("list_boards").then(() => {
        let board = state.boards.find(board => {
          return board.id == board_id;
        });
        return board
          ? board.name
          : to.meta && to.meta.title
          ? to.meta.title
          : title;
      });
    } else {
      let board = state.boards.find(board => {
        return board.id == board_id;
      });
      return board
        ? board.name
        : to.meta && to.meta.title
        ? to.meta.title
        : title;
    }
  }
};

function parseColumnValues(item_to_update, column_values) {
  column_values.map(column_value => {
    let title = column_value.title
      .replaceAll(".", "")
      .replaceAll(">", "")
      .toLowerCase()
      .trim();

    if (column_value.type == "date") {
      // can be due_date1, due_date2 etc
      if (
        column_value.text &&
        column_value.text != "" &&
        moment(column_value.text, SHORT_DATE).isValid()
      ) {
        column_value.text = moment(column_value.text).format(SHORT_DATE);
        if (title == "due date") {
          item_to_update.due_date = column_value.text;
        }
      } else {
        column_value.value = JSON.stringify({ date: "" });
      }
    } else if (title == "status") {
      item_to_update.status_column_id = column_value.id;
      item_to_update.status = column_value.text || "";

      item_to_update.status_color = stringToColor(
        item_to_update.status ? item_to_update.status : ""
      );
      for (status in Constants.STATUS) {
        let status_item = Constants.STATUS[status];
        let status_text = item_to_update.status
          ? item_to_update.status
              .replace(/[^a-zA-Z ]/g, "")
              .toLowerCase()
              .trim()
          : "";

        if (status_text == status_item.NAME) {
          item_to_update.status_color = status_item.COLOR;
        }
      }
      item_to_update.status_text_color = contrast(item_to_update.status_color);
    }
    // pending, progress, type column - colored
    else if (["pending", "progress", "type", "alert"].includes(title)) {
      // get random color based on text
      let text = column_value.text ? column_value.text.toLowerCase() : "";

      item_to_update[`${title}_color`] = stringToColor(text);
      item_to_update[`${title}_text_color`] = contrast(
        item_to_update[`${title}_color`]
      );
    }
  });
  return item_to_update;
}

function mapBoardItems(boards) {
  return boards.map(board => {
    if (board.items) {
      board.items.map(item => {
        item.column_values.map(column_value => {
          item = parseColumnValues(item, item.column_values);

          column_value.reset_text = column_value.text;

          if (column_value.id == "people" || column_value.id == "owner") {
            item.owner = column_value.text;
          }

          const title = column_value.title
            .replaceAll(".", "")
            .replaceAll(">", "")
            .toLowerCase()
            .trim();

          // used by dashboard
          if (title == "progress") {
            item.progress = column_value.text;
            item.progress_column_id = column_value.id;
          }
          if (title == "dri") {
            item.detail = column_value.text;
            item.detail_column_id = column_value.id;
          }
        });

        _.remove(item.column_values, r => {
          return (
            r.title.toLowerCase() == "last updated" ||
            r.title.toLowerCase() == "created date"
          );
        });
        // console.log("item", item);
      });
    }
  });
}
